// Row contracts of the native calendar (CALENDAR-C1; docs/design/2026-10-28-calendar.md §2.1):
// calendars, events, attendees, shares, follows, revisions and the ICS feed. Same conventions as
// `rows.ts`: keys are column names, nullability is the column's, enums are the CHECK's;
// `contract-drift.test.ts` binds each to its table.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  CalendarAttendeeResponse,
  CalendarEventSource,
  CalendarEventStatus,
  CalendarEventVisibility,
  CalendarKind,
  CalendarRevisionReason,
  CalendarShareGrantee,
  CalendarShareLevel,
  CalendarVisibility,
} from "./enums";
import { Timestamp, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

export const Calendar = z
  .object({
    company_id: Uuid,
    id: Uuid,
    kind: CalendarKind,
    team_id: Uuid.nullable(),
    owner_membership_id: Uuid.nullable(),
    view_id: Uuid.nullable(),
    name: z.string(),
    color: z.string().nullable(),
    visibility: CalendarVisibility,
    timezone: z.string().nullable(),
    archived_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Calendar" });
export type Calendar = z.infer<typeof Calendar>;

export const CalendarEvent = z
  .object({
    company_id: Uuid,
    id: Uuid,
    calendar_id: Uuid,
    title: z.string(),
    description: z.string().nullable(),
    location: z.string().nullable(),
    starts_at: Timestamp,
    ends_at: Timestamp,
    all_day: z.boolean(),
    timezone: z.string(),
    status: CalendarEventStatus,
    visibility: CalendarEventVisibility,
    organizer_membership_id: Uuid.nullable(),
    rrule: z.string().nullable(),
    series_until: Timestamp.nullable(),
    recurrence_parent_id: Uuid.nullable(),
    original_start_at: Timestamp.nullable(),
    source: CalendarEventSource,
    source_ref: Uuid.nullable(),
    version: z.int().min(1),
    last_external_sync_at: Timestamp.nullable(),
    cancelled_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "CalendarEvent" });
export type CalendarEvent = z.infer<typeof CalendarEvent>;

export const CalendarEventAttendee = z
  .object({
    company_id: Uuid,
    id: Uuid,
    event_id: Uuid,
    membership_id: Uuid.nullable(),
    email: z.string().nullable(),
    response: CalendarAttendeeResponse,
    optional: z.boolean(),
    responded_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "CalendarEventAttendee" });
export type CalendarEventAttendee = z.infer<typeof CalendarEventAttendee>;

export const CalendarShare = z
  .object({
    company_id: Uuid,
    id: Uuid,
    calendar_id: Uuid,
    grantee_kind: CalendarShareGrantee,
    grantee_id: Uuid,
    level: CalendarShareLevel,
    revoked_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "CalendarShare" });
export type CalendarShare = z.infer<typeof CalendarShare>;

export const CalendarFollow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    membership_id: Uuid,
    calendar_id: Uuid,
    shown: z.boolean(),
    color_override: z.string().nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "CalendarFollow" });
export type CalendarFollow = z.infer<typeof CalendarFollow>;

export const CalendarEventRevision = z
  .object({
    company_id: Uuid,
    id: Uuid,
    event_id: Uuid,
    version: z.int().min(1),
    snapshot: z.record(z.string(), z.unknown()),
    reason: CalendarRevisionReason,
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "CalendarEventRevision" });
export type CalendarEventRevision = z.infer<typeof CalendarEventRevision>;

/** The feed row without its token hash (the token is shown once, when the link is made). */
export const CalendarFeed = z
  .object({
    company_id: Uuid,
    id: Uuid,
    membership_id: Uuid,
    last_used_at: Timestamp.nullable(),
    revoked_at: Timestamp.nullable(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "CalendarFeed" });
export type CalendarFeed = z.infer<typeof CalendarFeed>;
